import { createContext, useContext, useState, useEffect } from 'react';

const translations = {
  en: {
    // Navigation
    'nav.home': 'Home',
    'nav.experiences': 'Experiences',
    'nav.cities': 'Cities',
    'nav.about': 'About',
    'nav.contact': 'Contact',
    
    // Hero
    'hero.tagline': 'By Invitation Only',
    'hero.title': 'The Brazil you could',
    'hero.title.highlight': 'never access alone',
    'hero.subtitle': 'International standards. Brazilian insider access.',
    'hero.subtitle2': 'For those who know that the best things aren\'t advertised.',
    'hero.cta': 'Enter Velmont',
    
    // Brand Intro
    'intro.tagline': 'Access, Discretion, Excellence',
    'intro.title': 'We don\'t sell packages.',
    'intro.title.highlight': 'We open doors.',
    'intro.description': 'Velmont Society exists for those who understand that true access cannot be purchased — it must be earned. We are a private network of curators, insiders, and trusted partners who transform desires into reality across Brazil\'s most compelling destinations.',
    'intro.motto': 'Not for everyone. By design.',
    
    // Pillars
    'pillars.tagline': 'What We Curate',
    'pillars.title': 'Three Dimensions of',
    'pillars.title.highlight': 'Privilege',
    'pillars.nightlife': 'Nightlife & Social',
    'pillars.nightlife.desc': 'Reserved tables at venues that don\'t take reservations. Doors that open before you arrive.',
    'pillars.private': 'Private Experiences',
    'pillars.private.desc': 'Villas without neighbors. Chefs who cook only for you. Moments designed around your rhythm.',
    'pillars.events': 'Events & Celebrations',
    'pillars.events.desc': 'Milestones that become legends. Every detail invisible until it matters.',
    
    // Trust
    'trust.network': 'Private Network',
    'trust.network.desc': 'Access earned through trust, not transactions.',
    'trust.partners': 'Verified Partners',
    'trust.partners.desc': 'Every connection personally cultivated over years.',
    'trust.discreet': 'Discreet Operations',
    'trust.discreet.desc': 'Your presence is known only to those who need to know.',
    
    // Experiences Preview
    'exp.tagline': 'A Glimpse Inside',
    'exp.title': 'Curated',
    'exp.title.highlight': 'Moments',
    'exp.viewall': 'Explore All',
    
    // Cities
    'cities.tagline': 'Where We Operate',
    'cities.title': 'Two Cities.',
    'cities.title.highlight': 'One Standard.',
    'cities.sp': 'São Paulo',
    'cities.sp.tagline': 'The pulse of South America',
    'cities.rio': 'Rio de Janeiro',
    'cities.rio.tagline': 'Where beauty commands attention',
    
    // CTA
    'cta.tagline': 'The Door Is Open',
    'cta.title': 'Every journey begins with a',
    'cta.title.highlight': 'conversation',
    'cta.description': 'We don\'t work with everyone. We work with those who value discretion, quality, and the kind of access that can\'t be Googled.',
    'cta.button': 'Begin the Conversation',
    
    // Footer
    'footer.tagline': 'The Brazil you could never access alone. International standards. Brazilian insider access.',
    'footer.motto': 'Access, Discretion, Excellence.',
    'footer.navigate': 'Navigate',
    'footer.destinations': 'Destinations',
    'footer.connect': 'Connect',
    'footer.rights': 'All rights reserved.',
    'footer.invitation': 'By invitation only.',
    
    // Contact
    'contact.tagline': 'Request Private Access',
    'contact.title': 'The Door Is',
    'contact.title.highlight': 'Open',
    'contact.description': 'Tell us what you\'re looking for. There\'s no commitment, no pressure — just a conversation to understand if we\'re the right fit for each other.',
    'contact.name': 'Your Name',
    'contact.name.placeholder': 'How should we address you?',
    'contact.email': 'Email Address',
    'contact.phone': 'Phone / WhatsApp',
    'contact.phone.placeholder': 'For faster communication',
    'contact.city': 'Destination',
    'contact.city.placeholder': 'Where are you drawn to?',
    'contact.experience': 'Interest',
    'contact.experience.placeholder': 'What brings you here?',
    'contact.message': 'Your Vision',
    'contact.message.placeholder': 'Dates, group size, ideas, or anything we should know...',
    'contact.submit': 'Request Private Access',
    'contact.whatsapp.title': 'Prefer to Talk?',
    'contact.whatsapp.subtitle': 'Direct Line via WhatsApp',
    'contact.whatsapp.description': 'For time-sensitive inquiries or if you simply prefer conversation over forms. Our team typically responds within the hour.',
    'contact.whatsapp.button': 'Open WhatsApp',
    'contact.next': 'What Happens Next',
    'contact.next.desc': 'A member of our team will personally review your request and reach out within 24-48 hours to discuss possibilities.',
    'contact.discretion': 'Complete Discretion',
    'contact.discretion.desc': 'Everything you share stays between us. We never discuss our clients, their plans, or their preferences. Period.',
    'contact.obligation': 'No Obligation',
    'contact.obligation.desc': 'This is a conversation, not a commitment. We\'ll be honest about what we can deliver — and what we can\'t.',
    'contact.success.title': 'Request',
    'contact.success.highlight': 'Received',
    'contact.success.message': 'Thank you for reaching out. A member of our team will review your request and be in touch within 24-48 hours.',
    'contact.success.note': 'We respond to every inquiry personally.',
    'contact.success.home': 'Return Home'
  },
  
  pt: {
    // Navigation
    'nav.home': 'Início',
    'nav.experiences': 'Experiências',
    'nav.cities': 'Cidades',
    'nav.about': 'Sobre',
    'nav.contact': 'Contato',
    
    // Hero
    'hero.tagline': 'Apenas por Convite',
    'hero.title': 'O Brasil que você',
    'hero.title.highlight': 'nunca acessaria sozinho',
    'hero.subtitle': 'Padrões internacionais. Acesso privilegiado brasileiro.',
    'hero.subtitle2': 'Para quem sabe que as melhores coisas não são anunciadas.',
    'hero.cta': 'Entre na Velmont',
    
    // Brand Intro
    'intro.tagline': 'Acesso, Discrição, Excelência',
    'intro.title': 'Não vendemos pacotes.',
    'intro.title.highlight': 'Abrimos portas.',
    'intro.description': 'A Velmont Society existe para aqueles que entendem que o verdadeiro acesso não pode ser comprado — deve ser conquistado. Somos uma rede privada de curadores, insiders e parceiros de confiança que transformam desejos em realidade nos destinos mais fascinantes do Brasil.',
    'intro.motto': 'Não é para todos. Por design.',
    
    // Pillars
    'pillars.tagline': 'O Que Curamos',
    'pillars.title': 'Três Dimensões de',
    'pillars.title.highlight': 'Privilégio',
    'pillars.nightlife': 'Vida Noturna & Social',
    'pillars.nightlife.desc': 'Mesas reservadas em locais que não aceitam reservas. Portas que se abrem antes de você chegar.',
    'pillars.private': 'Experiências Privadas',
    'pillars.private.desc': 'Villas sem vizinhos. Chefs que cozinham só para você. Momentos desenhados no seu ritmo.',
    'pillars.events': 'Eventos & Celebrações',
    'pillars.events.desc': 'Marcos que se tornam lendas. Cada detalhe invisível até que importe.',
    
    // Trust
    'trust.network': 'Rede Privada',
    'trust.network.desc': 'Acesso conquistado por confiança, não por transações.',
    'trust.partners': 'Parceiros Verificados',
    'trust.partners.desc': 'Cada conexão cultivada pessoalmente ao longo dos anos.',
    'trust.discreet': 'Operações Discretas',
    'trust.discreet.desc': 'Sua presença é conhecida apenas por quem precisa saber.',
    
    // Experiences Preview
    'exp.tagline': 'Um Vislumbre',
    'exp.title': 'Momentos',
    'exp.title.highlight': 'Curados',
    'exp.viewall': 'Explorar Todos',
    
    // Cities
    'cities.tagline': 'Onde Operamos',
    'cities.title': 'Duas Cidades.',
    'cities.title.highlight': 'Um Padrão.',
    'cities.sp': 'São Paulo',
    'cities.sp.tagline': 'O pulso da América do Sul',
    'cities.rio': 'Rio de Janeiro',
    'cities.rio.tagline': 'Onde a beleza comanda atenção',
    
    // CTA
    'cta.tagline': 'A Porta Está Aberta',
    'cta.title': 'Toda jornada começa com uma',
    'cta.title.highlight': 'conversa',
    'cta.description': 'Não trabalhamos com todos. Trabalhamos com aqueles que valorizam discrição, qualidade e o tipo de acesso que não se encontra no Google.',
    'cta.button': 'Iniciar a Conversa',
    
    // Footer
    'footer.tagline': 'O Brasil que você nunca acessaria sozinho. Padrões internacionais. Acesso privilegiado brasileiro.',
    'footer.motto': 'Acesso, Discrição, Excelência.',
    'footer.navigate': 'Navegar',
    'footer.destinations': 'Destinos',
    'footer.connect': 'Conectar',
    'footer.rights': 'Todos os direitos reservados.',
    'footer.invitation': 'Apenas por convite.',
    
    // Contact
    'contact.tagline': 'Solicitar Acesso Privado',
    'contact.title': 'A Porta Está',
    'contact.title.highlight': 'Aberta',
    'contact.description': 'Conte-nos o que você procura. Sem compromisso, sem pressão — apenas uma conversa para entender se somos a combinação certa.',
    'contact.name': 'Seu Nome',
    'contact.name.placeholder': 'Como devemos chamá-lo?',
    'contact.email': 'E-mail',
    'contact.phone': 'Telefone / WhatsApp',
    'contact.phone.placeholder': 'Para comunicação mais rápida',
    'contact.city': 'Destino',
    'contact.city.placeholder': 'Para onde você quer ir?',
    'contact.experience': 'Interesse',
    'contact.experience.placeholder': 'O que o traz aqui?',
    'contact.message': 'Sua Visão',
    'contact.message.placeholder': 'Datas, tamanho do grupo, ideias ou qualquer coisa que devemos saber...',
    'contact.submit': 'Solicitar Acesso Privado',
    'contact.whatsapp.title': 'Prefere Conversar?',
    'contact.whatsapp.subtitle': 'Linha Direta via WhatsApp',
    'contact.whatsapp.description': 'Para consultas urgentes ou se você simplesmente prefere conversar. Nossa equipe geralmente responde em uma hora.',
    'contact.whatsapp.button': 'Abrir WhatsApp',
    'contact.next': 'O Que Acontece Depois',
    'contact.next.desc': 'Um membro da nossa equipe revisará pessoalmente seu pedido e entrará em contato em 24-48 horas para discutir possibilidades.',
    'contact.discretion': 'Discrição Total',
    'contact.discretion.desc': 'Tudo que você compartilha fica entre nós. Nunca discutimos nossos clientes, seus planos ou suas preferências. Ponto final.',
    'contact.obligation': 'Sem Compromisso',
    'contact.obligation.desc': 'Esta é uma conversa, não um compromisso. Seremos honestos sobre o que podemos entregar — e o que não podemos.',
    'contact.success.title': 'Solicitação',
    'contact.success.highlight': 'Recebida',
    'contact.success.message': 'Obrigado por entrar em contato. Um membro da nossa equipe revisará seu pedido e entrará em contato em 24-48 horas.',
    'contact.success.note': 'Respondemos a cada consulta pessoalmente.',
    'contact.success.home': 'Voltar ao Início'
  },
  
  es: {
    // Navigation
    'nav.home': 'Inicio',
    'nav.experiences': 'Experiencias',
    'nav.cities': 'Ciudades',
    'nav.about': 'Nosotros',
    'nav.contact': 'Contacto',
    
    // Hero
    'hero.tagline': 'Solo por Invitación',
    'hero.title': 'El Brasil al que',
    'hero.title.highlight': 'nunca accederías solo',
    'hero.subtitle': 'Estándares internacionales. Acceso privilegiado brasileño.',
    'hero.subtitle2': 'Para quienes saben que las mejores cosas no se anuncian.',
    'hero.cta': 'Entrar a Velmont',
    
    // Brand Intro
    'intro.tagline': 'Acceso, Discreción, Excelencia',
    'intro.title': 'No vendemos paquetes.',
    'intro.title.highlight': 'Abrimos puertas.',
    'intro.description': 'Velmont Society existe para aquellos que entienden que el verdadero acceso no se puede comprar — debe ganarse. Somos una red privada de curadores, insiders y socios de confianza que transforman deseos en realidad en los destinos más fascinantes de Brasil.',
    'intro.motto': 'No es para todos. Por diseño.',
    
    // Pillars
    'pillars.tagline': 'Lo Que Curamos',
    'pillars.title': 'Tres Dimensiones de',
    'pillars.title.highlight': 'Privilegio',
    'pillars.nightlife': 'Vida Nocturna & Social',
    'pillars.nightlife.desc': 'Mesas reservadas en lugares que no aceptan reservas. Puertas que se abren antes de llegar.',
    'pillars.private': 'Experiencias Privadas',
    'pillars.private.desc': 'Villas sin vecinos. Chefs que cocinan solo para ti. Momentos diseñados a tu ritmo.',
    'pillars.events': 'Eventos & Celebraciones',
    'pillars.events.desc': 'Hitos que se convierten en leyendas. Cada detalle invisible hasta que importa.',
    
    // Trust
    'trust.network': 'Red Privada',
    'trust.network.desc': 'Acceso ganado por confianza, no por transacciones.',
    'trust.partners': 'Socios Verificados',
    'trust.partners.desc': 'Cada conexión cultivada personalmente durante años.',
    'trust.discreet': 'Operaciones Discretas',
    'trust.discreet.desc': 'Tu presencia es conocida solo por quienes necesitan saberlo.',
    
    // Experiences Preview
    'exp.tagline': 'Un Vistazo',
    'exp.title': 'Momentos',
    'exp.title.highlight': 'Curados',
    'exp.viewall': 'Explorar Todos',
    
    // Cities
    'cities.tagline': 'Donde Operamos',
    'cities.title': 'Dos Ciudades.',
    'cities.title.highlight': 'Un Estándar.',
    'cities.sp': 'São Paulo',
    'cities.sp.tagline': 'El pulso de Sudamérica',
    'cities.rio': 'Río de Janeiro',
    'cities.rio.tagline': 'Donde la belleza comanda atención',
    
    // CTA
    'cta.tagline': 'La Puerta Está Abierta',
    'cta.title': 'Todo viaje comienza con una',
    'cta.title.highlight': 'conversación',
    'cta.description': 'No trabajamos con todos. Trabajamos con quienes valoran la discreción, la calidad y el tipo de acceso que no se encuentra en Google.',
    'cta.button': 'Iniciar la Conversación',
    
    // Footer
    'footer.tagline': 'El Brasil al que nunca accederías solo. Estándares internacionales. Acceso privilegiado brasileño.',
    'footer.motto': 'Acceso, Discreción, Excelencia.',
    'footer.navigate': 'Navegar',
    'footer.destinations': 'Destinos',
    'footer.connect': 'Conectar',
    'footer.rights': 'Todos los derechos reservados.',
    'footer.invitation': 'Solo por invitación.',
    
    // Contact
    'contact.tagline': 'Solicitar Acceso Privado',
    'contact.title': 'La Puerta Está',
    'contact.title.highlight': 'Abierta',
    'contact.description': 'Cuéntanos lo que buscas. Sin compromiso, sin presión — solo una conversación para entender si somos la combinación correcta.',
    'contact.name': 'Tu Nombre',
    'contact.name.placeholder': '¿Cómo debemos llamarte?',
    'contact.email': 'Correo Electrónico',
    'contact.phone': 'Teléfono / WhatsApp',
    'contact.phone.placeholder': 'Para comunicación más rápida',
    'contact.city': 'Destino',
    'contact.city.placeholder': '¿A dónde quieres ir?',
    'contact.experience': 'Interés',
    'contact.experience.placeholder': '¿Qué te trae aquí?',
    'contact.message': 'Tu Visión',
    'contact.message.placeholder': 'Fechas, tamaño del grupo, ideas o cualquier cosa que debamos saber...',
    'contact.submit': 'Solicitar Acceso Privado',
    'contact.whatsapp.title': '¿Prefieres Hablar?',
    'contact.whatsapp.subtitle': 'Línea Directa vía WhatsApp',
    'contact.whatsapp.description': 'Para consultas urgentes o si simplemente prefieres conversar. Nuestro equipo generalmente responde en una hora.',
    'contact.whatsapp.button': 'Abrir WhatsApp',
    'contact.next': 'Qué Sigue',
    'contact.next.desc': 'Un miembro de nuestro equipo revisará personalmente tu solicitud y se pondrá en contacto en 24-48 horas para discutir posibilidades.',
    'contact.discretion': 'Discreción Total',
    'contact.discretion.desc': 'Todo lo que compartas queda entre nosotros. Nunca discutimos a nuestros clientes, sus planes o sus preferencias. Punto.',
    'contact.obligation': 'Sin Compromiso',
    'contact.obligation.desc': 'Esta es una conversación, no un compromiso. Seremos honestos sobre lo que podemos entregar — y lo que no.',
    'contact.success.title': 'Solicitud',
    'contact.success.highlight': 'Recibida',
    'contact.success.message': 'Gracias por contactarnos. Un miembro de nuestro equipo revisará tu solicitud y se pondrá en contacto en 24-48 horas.',
    'contact.success.note': 'Respondemos a cada consulta personalmente.',
    'contact.success.home': 'Volver al Inicio'
  },
  
  fr: {
    // Navigation
    'nav.home': 'Accueil',
    'nav.experiences': 'Expériences',
    'nav.cities': 'Villes',
    'nav.about': 'À Propos',
    'nav.contact': 'Contact',
    
    // Hero
    'hero.tagline': 'Sur Invitation Uniquement',
    'hero.title': 'Le Brésil auquel vous',
    'hero.title.highlight': 'n\'accéderiez jamais seul',
    'hero.subtitle': 'Standards internationaux. Accès privilégié brésilien.',
    'hero.subtitle2': 'Pour ceux qui savent que les meilleures choses ne sont pas annoncées.',
    'hero.cta': 'Entrer chez Velmont',
    
    // Brand Intro
    'intro.tagline': 'Accès, Discrétion, Excellence',
    'intro.title': 'Nous ne vendons pas de forfaits.',
    'intro.title.highlight': 'Nous ouvrons des portes.',
    'intro.description': 'Velmont Society existe pour ceux qui comprennent que le véritable accès ne s\'achète pas — il se mérite. Nous sommes un réseau privé de curateurs, d\'initiés et de partenaires de confiance qui transforment les désirs en réalité dans les destinations les plus fascinantes du Brésil.',
    'intro.motto': 'Pas pour tout le monde. Par conception.',
    
    // Pillars
    'pillars.tagline': 'Ce Que Nous Curons',
    'pillars.title': 'Trois Dimensions de',
    'pillars.title.highlight': 'Privilège',
    'pillars.nightlife': 'Vie Nocturne & Social',
    'pillars.nightlife.desc': 'Tables réservées dans des lieux qui n\'acceptent pas de réservations. Des portes qui s\'ouvrent avant votre arrivée.',
    'pillars.private': 'Expériences Privées',
    'pillars.private.desc': 'Villas sans voisins. Chefs qui cuisinent uniquement pour vous. Moments conçus autour de votre rythme.',
    'pillars.events': 'Événements & Célébrations',
    'pillars.events.desc': 'Des jalons qui deviennent des légendes. Chaque détail invisible jusqu\'à ce qu\'il compte.',
    
    // Trust
    'trust.network': 'Réseau Privé',
    'trust.network.desc': 'Accès gagné par la confiance, pas par les transactions.',
    'trust.partners': 'Partenaires Vérifiés',
    'trust.partners.desc': 'Chaque connexion cultivée personnellement au fil des années.',
    'trust.discreet': 'Opérations Discrètes',
    'trust.discreet.desc': 'Votre présence n\'est connue que de ceux qui doivent le savoir.',
    
    // Experiences Preview
    'exp.tagline': 'Un Aperçu',
    'exp.title': 'Moments',
    'exp.title.highlight': 'Curés',
    'exp.viewall': 'Explorer Tout',
    
    // Cities
    'cities.tagline': 'Où Nous Opérons',
    'cities.title': 'Deux Villes.',
    'cities.title.highlight': 'Un Standard.',
    'cities.sp': 'São Paulo',
    'cities.sp.tagline': 'Le pouls de l\'Amérique du Sud',
    'cities.rio': 'Rio de Janeiro',
    'cities.rio.tagline': 'Où la beauté commande l\'attention',
    
    // CTA
    'cta.tagline': 'La Porte Est Ouverte',
    'cta.title': 'Tout voyage commence par une',
    'cta.title.highlight': 'conversation',
    'cta.description': 'Nous ne travaillons pas avec tout le monde. Nous travaillons avec ceux qui valorisent la discrétion, la qualité et le type d\'accès qu\'on ne trouve pas sur Google.',
    'cta.button': 'Commencer la Conversation',
    
    // Footer
    'footer.tagline': 'Le Brésil auquel vous n\'accéderiez jamais seul. Standards internationaux. Accès privilégié brésilien.',
    'footer.motto': 'Accès, Discrétion, Excellence.',
    'footer.navigate': 'Naviguer',
    'footer.destinations': 'Destinations',
    'footer.connect': 'Connecter',
    'footer.rights': 'Tous droits réservés.',
    'footer.invitation': 'Sur invitation uniquement.',
    
    // Contact
    'contact.tagline': 'Demander un Accès Privé',
    'contact.title': 'La Porte Est',
    'contact.title.highlight': 'Ouverte',
    'contact.description': 'Dites-nous ce que vous recherchez. Sans engagement, sans pression — juste une conversation pour comprendre si nous sommes faits l\'un pour l\'autre.',
    'contact.name': 'Votre Nom',
    'contact.name.placeholder': 'Comment devons-nous vous appeler?',
    'contact.email': 'Adresse E-mail',
    'contact.phone': 'Téléphone / WhatsApp',
    'contact.phone.placeholder': 'Pour une communication plus rapide',
    'contact.city': 'Destination',
    'contact.city.placeholder': 'Où souhaitez-vous aller?',
    'contact.experience': 'Intérêt',
    'contact.experience.placeholder': 'Qu\'est-ce qui vous amène ici?',
    'contact.message': 'Votre Vision',
    'contact.message.placeholder': 'Dates, taille du groupe, idées ou tout ce que nous devrions savoir...',
    'contact.submit': 'Demander un Accès Privé',
    'contact.whatsapp.title': 'Préférez Parler?',
    'contact.whatsapp.subtitle': 'Ligne Directe via WhatsApp',
    'contact.whatsapp.description': 'Pour les demandes urgentes ou si vous préférez simplement converser. Notre équipe répond généralement dans l\'heure.',
    'contact.whatsapp.button': 'Ouvrir WhatsApp',
    'contact.next': 'La Suite',
    'contact.next.desc': 'Un membre de notre équipe examinera personnellement votre demande et vous contactera sous 24-48 heures pour discuter des possibilités.',
    'contact.discretion': 'Discrétion Totale',
    'contact.discretion.desc': 'Tout ce que vous partagez reste entre nous. Nous ne discutons jamais de nos clients, de leurs projets ou de leurs préférences. Point.',
    'contact.obligation': 'Sans Engagement',
    'contact.obligation.desc': 'C\'est une conversation, pas un engagement. Nous serons honnêtes sur ce que nous pouvons livrer — et ce que nous ne pouvons pas.',
    'contact.success.title': 'Demande',
    'contact.success.highlight': 'Reçue',
    'contact.success.message': 'Merci de nous avoir contactés. Un membre de notre équipe examinera votre demande et vous contactera sous 24-48 heures.',
    'contact.success.note': 'Nous répondons personnellement à chaque demande.',
    'contact.success.home': 'Retour à l\'Accueil'
  }
};

// Language names for display
const languageNames = {
  en: 'English',
  pt: 'Português',
  es: 'Español',
  fr: 'Français'
};

// Detect browser language
const detectBrowserLanguage = () => {
  const browserLang = navigator.language || navigator.userLanguage;
  const langCode = browserLang.split('-')[0].toLowerCase();
  
  // Map browser language to supported languages
  if (langCode === 'pt') return 'pt';
  if (langCode === 'es') return 'es';
  if (langCode === 'fr') return 'fr';
  return 'en'; // Default to English
};

const LanguageContext = createContext();

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    // Check if user has previously selected a language
    const saved = localStorage.getItem('velmont_language');
    if (saved && translations[saved]) {
      return saved;
    }
    // Otherwise detect from browser
    return detectBrowserLanguage();
  });

  useEffect(() => {
    localStorage.setItem('velmont_language', language);
    document.documentElement.lang = language;
  }, [language]);

  const t = (key) => {
    return translations[language]?.[key] || translations['en']?.[key] || key;
  };

  const cycleLanguage = () => {
    const langs = ['en', 'pt', 'es', 'fr'];
    const currentIndex = langs.indexOf(language);
    const nextIndex = (currentIndex + 1) % langs.length;
    setLanguage(langs[nextIndex]);
  };

  const availableLanguages = Object.keys(translations);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      cycleLanguage, 
      t, 
      languageNames,
      availableLanguages 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

export default LanguageContext;
